import * as React from "react";
import { Instagram, Facebook } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
type SupportedLanguage = "en" | "zh" | "fr" | "es" | "de";
function useWvcLanguage(): SupportedLanguage {
  const [lang, setLang] = React.useState<SupportedLanguage>(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("wvc_lang") as SupportedLanguage | null;
    return saved && ["en", "zh", "fr", "es", "de"].includes(saved) ? saved : "en";
  });
  React.useEffect(() => {
    const handleStorage = () => {
      const current = localStorage.getItem("wvc_lang") as SupportedLanguage | null;
      if (current && ["en", "zh", "fr", "es", "de"].includes(current)) {
        setLang(current);
      }
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("wvc_language_change", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("wvc_language_change", handleStorage);
    };
  }, []);
  return lang;
}
const translations = {
  en: {
    tagline: "Modern silhouettes crafted for the confident woman. Thoughtfully tailored garments made to endure beyond seasons.",
    location: "London & North America",
    subtitle: "Curated collections and intentional craftsmanship.",
    customerCareHeading: "Customer Care",
    aboutHeading: "About Us",
    legalHeading: "Legal & Privacy",
    contact: "Contact",
    customerService: "Customer Service",
    shippingReturns: "Shipping & Returns",
    sizeGuide: "Size Guide",
    faq: "FAQ",
    ourStory: "Our Story",
    careers: "Careers",
    lookbook: "Editorial Lookbook",
    sustainability: "Sustainability",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    cookiePolicy: "Cookie Policy",
    follow: "Follow",
    acceptedPayments: "Accepted Payments:",
    copyright: "© 2025 yishengfushi. All rights reserved."
  },
  zh: {
    tagline: "为自信女性设计的现代剪裁。经过精心裁剪的服装经久耐穿。",
    location: "伦敦及北美",
    subtitle: "精心策划的系列和精心的工艺。",
    customerCareHeading: "客户服务",
    aboutHeading: "关于我们",
    legalHeading: "法律与隐私",
    contact: "联系",
    customerService: "客户服务",
    shippingReturns: "运送与退货",
    sizeGuide: "尺码指南",
    faq: "常见问题",
    ourStory: "我们的故事",
    careers: "职业",
    lookbook: "编辑查看簿",
    sustainability: "可持续性",
    privacyPolicy: "隐私政策",
    termsOfService: "服务条款",
    cookiePolicy: "Cookie政策",
    follow: "关注",
    acceptedPayments: "接受的付款:",
    copyright: "© 2025 yishengfushi。版权所有。"
  },
  fr: {
    tagline: "Silhouettes modernes créées pour la femme confiante. Des vêtements taillés avec soin pour durer au-delà des saisons.",
    location: "Londres et Amérique du Nord",
    subtitle: "Collections curées et artisanat intentionnel.",
    customerCareHeading: "Service Client",
    aboutHeading: "À Propos",
    legalHeading: "Légal et Confidentialité",
    contact: "Contact",
    customerService: "Service Client",
    shippingReturns: "Livraison et Retours",
    sizeGuide: "Guide des Tailles",
    faq: "FAQ",
    ourStory: "Notre Histoire",
    careers: "Carrières",
    lookbook: "Lookbook Éditorial",
    sustainability: "Durabilité",
    privacyPolicy: "Politique de Confidentialité",
    termsOfService: "Conditions d'Utilisation",
    cookiePolicy: "Politique relative aux Cookies",
    follow: "Suivre",
    acceptedPayments: "Paiements Acceptés:",
    copyright: "© 2025 yishengfushi. Tous droits réservés."
  },
  es: {
    tagline: "Siluetas modernas diseñadas para la mujer segura de sí misma. Prendas cuidadosamente confeccionadas para perdurar más allá de las estaciones.",
    location: "Londres y América del Norte",
    subtitle: "Colecciones curadas y artesanía intencional.",
    customerCareHeading: "Servicio al Cliente",
    aboutHeading: "Acerca de Nosotros",
    legalHeading: "Legal y Privacidad",
    contact: "Contacto",
    customerService: "Servicio al Cliente",
    shippingReturns: "Envío y Devoluciones",
    sizeGuide: "Guía de Tallas",
    faq: "Preguntas Frecuentes",
    ourStory: "Nuestra Historia",
    careers: "Carreras",
    lookbook: "Lookbook Editorial",
    sustainability: "Sostenibilidad",
    privacyPolicy: "Política de Privacidad",
    termsOfService: "Términos de Servicio",
    cookiePolicy: "Política de Cookies",
    follow: "Seguir",
    acceptedPayments: "Pagos Aceptados:",
    copyright: "© 2025 yishengfushi. Todos los derechos reservados."
  },
  de: {
    tagline: "Moderne Silhouetten für die selbstbewusste Frau. Sorgfältig verarbeitete Kleidung, die über Saisons hinweg hält.",
    location: "London und Nordamerika",
    subtitle: "Kuratierte Kollektionen und bewusste Handwerkskunst.",
    customerCareHeading: "Kundenservice",
    aboutHeading: "Über Uns",
    legalHeading: "Rechtliches und Datenschutz",
    contact: "Kontakt",
    customerService: "Kundenservice",
    shippingReturns: "Versand und Rückgaben",
    sizeGuide: "Größentabelle",
    faq: "Häufig Gestellte Fragen",
    ourStory: "Unsere Geschichte",
    careers: "Karrieren",
    lookbook: "Redaktionelles Lookbook",
    sustainability: "Nachhaltigkeit",
    privacyPolicy: "Datenschutzrichtlinie",
    termsOfService: "Nutzungsbedingungen",
    cookiePolicy: "Cookie-Richtlinie",
    follow: "Folgen",
    acceptedPayments: "Akzeptierte Zahlungen:",
    copyright: "© 2025 yishengfushi. Alle Rechte vorbehalten."
  }
};
const socialLinks = [{
  name: "Instagram",
  to: "https://instagram.com",
  icon: Instagram
}, {
  name: "Pinterest",
  to: "https://pinterest.com",
  icon: (props: React.SVGProps<SVGSVGElement>) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" className={props.className || "size-4"}>
        <line x1="12" y1="9" x2="12" y2="21" />
        <path d="M8 12a4 4 0 1 1 8 0c0 3-2 5.5-4 8" />
        <circle cx="12" cy="12" r="10" />
      </svg>
}, {
  name: "Facebook",
  to: "https://facebook.com",
  icon: Facebook
}];
const paymentMethods = ["Visa", "Mastercard", "Amex", "PayPal", "Apple Pay", "Google Pay"];
export default function Footer() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const customerCareLinks = [{
    label: t.customerService,
    to: "/CustomerservicePage"
  }, {
    label: t.shippingReturns,
    to: "#"
  }, {
    label: t.sizeGuide,
    to: "#"
  }, {
    label: t.faq,
    to: "#"
  }];
  const aboutLinks = [{
    label: t.ourStory,
    to: "#"
  }, {
    label: t.careers,
    to: "#"
  }, {
    label: t.lookbook,
    to: "/ShopPage"
  }, {
    label: t.sustainability,
    to: "#"
  }];
  const legalLinks = [{
    label: t.privacyPolicy,
    to: "#"
  }, {
    label: t.termsOfService,
    to: "#"
  }, {
    label: t.cookiePolicy,
    to: "#"
  }];
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border">
      <div className="mx-auto max-w-[1360px] px-6 py-16 sm:px-10 lg:px-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10">
          
          {/* Column 1: Brand & Statement */}
          <div className="lg:col-span-4 flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <Link to="/" className="inline-block" aria-label="yishengfushi Home">
                <WvcLogo className="h-7 w-auto" />
              </Link>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-sm font-sans font-light">
                {t.tagline}
              </p>
            </div>
            <div className="space-y-2 pt-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans font-semibold">
                {t.location}
              </p>
              <p className="text-xs text-muted-foreground font-sans">
                {t.subtitle}
              </p>
            </div>
            <div className="space-y-2 pt-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans font-semibold">
                {t.contact}
              </p>
              <a href="tel:18748781290" className="block text-xs text-muted-foreground font-sans hover:text-foreground transition-colors">
                18748781290
              </a>
              <a href="mailto:yishengqiye2026@163.com" className="block text-xs text-muted-foreground font-sans hover:text-foreground transition-colors">
                yishengqiye2026@163.com
              </a>
              <p className="text-xs text-muted-foreground font-sans">
                贵州省六盘水市盘州市亦资街道新华能源国际酒店28楼11号房
              </p>
            </div>
          </div>

          {/* Column 2: Customer Care */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-widest font-sans font-semibold text-foreground">
              {t.customerCareHeading}
            </h3>
            <ul className="space-y-3">
              {customerCareLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 inline-flex items-center gap-1 group font-sans">
                    <span>{item.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 3: About Us */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs uppercase tracking-widest font-sans font-semibold text-foreground">
              {t.aboutHeading}
            </h3>
            <ul className="space-y-3">
              {aboutLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 inline-flex items-center gap-1 group font-sans">
                    <span>{item.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 4: Legal & Social */}
          <div className="lg:col-span-3 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xs uppercase tracking-widest font-sans font-semibold text-foreground">
                {t.legalHeading}
              </h3>
              <ul className="space-y-3">
                {legalLinks.map((item, i) => <li key={item.label} data-index={i}>
                    <Link to={item.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 inline-flex items-center gap-1 group font-sans">
                      <span>{item.label}</span>
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div className="space-y-3 pt-2">
              <h4 className="text-xs uppercase tracking-widest font-sans font-semibold text-foreground">
                {t.follow}
              </h4>
              <div className="flex items-center gap-3">
                {socialLinks.map((social, i) => {
                const IconComponent = social.icon;
                return <Link key={social.name} data-index={i} to={social.to} newTab aria-label={social.name} className="size-9 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground transition-colors duration-200">
                      <IconComponent className="size-4" />
                    </Link>;
              })}
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-border" />

        {/* Bottom Bar */}
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between text-xs text-muted-foreground font-sans">
          
          {/* Payment Methods */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground mr-1">
              {t.acceptedPayments}
            </span>
            {paymentMethods.map((method, i) => <span key={method} data-index={i} className="px-2.5 py-1 text-[11px] font-medium border border-border bg-card text-foreground">
                {method}
              </span>)}
          </div>

          {/* Copyright */}
          <div className="flex items-center gap-1">
            <p>{t.copyright}</p>
          </div>
        </div>
      </div>
    </section>;
}